
import {defineComponent, onMounted, PropType, reactive, ref, watch} from "vue";
import InputText from "primevue/inputtext";
import { required } from "@vuelidate/validators";
import { useVuelidate } from "@vuelidate/core";
import FileUpload from "@/components/DexFileUpload.vue";
import {useI18n} from "vue-i18n";
import {UploadFile} from "@/shims-prime-vue";
import Dropdown from "primevue/dropdown";
import Checkbox from "primevue/checkbox";

export default defineComponent({
	name: "MasterDataUploadForm",
	components: {
		FileUpload, InputText, Dropdown, Checkbox,
	},
	props: {
		showErrorMessage: {
			type: Boolean,
		},
		selectedFiles: {
			type: Array as PropType<UploadFile[]>,
			default: () => [],
		},
	},
	emits: ['update', 'fileUploader'],
	setup(props, {emit}) {
		const {t} = useI18n();
		/** Current Object of all input-fields */
		const value = reactive<any>({csvSeparator: ';', fileType: 'csv', skipErrors: true});

		/** Determines the required rules for validation */
		const rules = {
			csvSeparator: { required },
		}

		/** List with Available File-Types */
		const fileTypes = [
			{
				id: 'csv',
				description: t('Squeeze.MasterData.CSV'),
			},
			{
				id: 'json',
				description: t('Squeeze.MasterData.JSON'),
			},
		]

		/** Currently active File-Type */
		const activeFileType = ref('csv');

		/** List of all files */
		const files = ref<UploadFile[]>([]);

		/** Progress of all uploaded documents */
		const progress = ref(0);

		/** Use Vuelidate */
		const v$ = useVuelidate(rules, value);

		/** Label of the Upload */
		const uploadLabel = ref(t("Squeeze.General.Upload"));

		/** Should the upload button be disabled?  */
		const disableUploadButton = ref<boolean>(false);

		/** Watch prop at set value object, because props are not allowed to be mutated */
		watch(() => props.selectedFiles, () => {
			files.value = props.selectedFiles;
			uploadLabel.value = t("Squeeze.General.Upload") + " (" + files.value.filter(file => file.uploadFinished).length + "/" + files.value.length + ")";
		})

		onMounted(async () => {
			// Emit Validated-Values onMounted. Otherwise there can be validation-errors when editing multiple entries
			await v$.value.$validate();
			emit("update", value, v$.value.$invalid);
		});

		/** Triggered on change of any field */
		const update = () => {
			// set csv separator, when file type is json
			if (value.fileType === 'json') {
				value.csvSeparator = ';';
				disableUploadButton.value = false;
			} else {
				disableUploadButton.value = v$.value.$invalid;
			}

			emit("update", value, v$.value.$invalid);
		}

		/**
		 * Triggered when Files are selected
		 */
		const onSelectFiles =  async (event: any) => {
			// Reset array and only take last entry of the selected files-array which is too big if selected multiple times
			files.value = [];
			files.value[0] = event.files[event.files.length - 1];
			uploadLabel.value = t("Squeeze.General.Upload") + " (0/1)";
		}

		/**
		 * Triggered when the File-Upload is triggered.
		 * @param event
		 */
		const fileUploader = (event: any) => {
			emit("fileUploader", event.files, value.fileType, value.csvSeparator, value.skipErrors);
		}

		/**
		 * Is triggered when the "clear" button is pressed in the Upload-Component
		 */
		const clearFiles = () => {
			uploadLabel.value = t("Squeeze.General.Upload");
		}

		/**
		 * Is triggered when a single file is removed from upload
		 * @param event
		 */
		const removeFile = (event: any) => {
			files.value = event.files;
			uploadLabel.value = t("Squeeze.General.Upload") + " (" + files.value.filter(file => file.uploadFinished).length + "/" + files.value.length + ")";
		}

		return {
			value,
			props,
			v$,
			InputText,
			progress,
			fileTypes,
			activeFileType,
			files,
			uploadLabel,
			disableUploadButton,
			removeFile,
			update,
			fileUploader,
			onSelectFiles,
			clearFiles,
		}
	},
});
